/**
 * A place one release behind opens on the build that matches it (yourplace
 * yp-4 phase 3, docs/YOUR_PLACE_SPEC.md §6, "Offline during a release").
 *
 * karr.pro always serves the newest build, and the newest build may read a
 * column a place that missed a release does not have yet. Cloudflare Pages
 * keeps every deployment at its own URL, so the directory (yp-5), which knows
 * the place's release, stores at login which deployment matches it:
 *
 *   localStorage['karr.place'] = { release, behind, build_url }
 *
 * and this sends the browser there before anything draws. Nothing is asked of
 * the network: no place is stored until yp-5, so today it never redirects.
 * Only an https deployment of Karr's own Pages project is followed, never a
 * stored address anywhere else, and never the address already open.
 */
export const PLACE_KEY = 'karr.place';

/**
 * Karr's Cloudflare Pages project (scripts/deploy.mjs, project `karr`), whose
 * host Cloudflare named karr-2sq.pages.dev; each deployment is <id>.karr-2sq.pages.dev.
 */
const PAGES_HOST = /^(?:[a-z0-9-]+\.)?karr-2sq\.pages\.dev$/;

interface Here { origin: string; pathname: string; search: string; hash: string }

/** Where to go instead of here, or null to stay. Pure. */
export function olderBuildTarget(stored: string | null, here: Here): string | null {
    if (!stored) return null;
    let place: { release?: unknown; behind?: unknown; build_url?: unknown };
    try {
        place = JSON.parse(stored);
    } catch {
        return null;
    }
    if (place?.behind !== true || typeof place.build_url !== 'string') return null;
    let url: URL;
    try {
        url = new URL(place.build_url);
    } catch {
        return null;
    }
    if (url.protocol !== 'https:' || !PAGES_HOST.test(url.hostname) || url.origin === here.origin) return null;
    return `${url.origin}${here.pathname}${here.search}${here.hash}`;
}

/** Go to the matching build when this place is behind. Returns true when it is leaving. */
export function followPlaceRelease(): boolean {
    let stored: string | null = null;
    try {
        stored = window.localStorage.getItem(PLACE_KEY);
    } catch {
        return false;
    }
    const to = olderBuildTarget(stored, window.location);
    if (!to) return false;
    window.location.replace(to);
    return true;
}
